import React, { useState, useEffect } from 'react';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!localStorage.getItem('pwa-install-dismissed')) setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    if (iOS && !isStandalone) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
        clearTimeout(timer);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setIsInstalled(true);
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) setShowPrompt(false);
  }, []);

  if (isInstalled || !showPrompt) return null;

  return (
    <div className="no-print pwa-install-prompt" data-testid="pwa-install-prompt" style={styles.overlay}>
      <div style={styles.banner}>
        <div style={styles.content}>
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <rect width="40" height="40" rx="8" fill="#3E2C6E"/>
            <rect x="10" y="14" width="12" height="10" rx="2" fill="white" opacity="0.9"/>
            <rect x="10" y="14" width="12" height="4" rx="2" fill="#E0A526"/>
          </svg>
          <div style={styles.text}>
            <div style={styles.title}>ثبّت تطبيق فكر نوفا</div>
            <div style={styles.subtitle}>
              {isIOS
                ? 'اضغط على زر المشاركة ‎⬆️ ثم اختر "إضافة إلى الشاشة الرئيسية"'
                : 'أضف التطبيق إلى شاشتك الرئيسية للوصول السريع'}
            </div>
          </div>
        </div>
        <div style={styles.actions}>
          {!isIOS && (
            <button style={styles.installBtn} onClick={handleInstall}>
              تثبيت
            </button>
          )}
          <button type="button" data-testid="pwa-install-dismiss" style={styles.dismissBtn} onClick={handleDismiss}>
            لاحقاً
          </button>
        </div>
      </div>
      <style>{`
        @media print {
          .no-print,
          .pwa-install-prompt {
            display: none !important;
            visibility: hidden !important;
          }
        }
      `}</style>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    padding: 'var(--sp-md, 12px)',
    direction: 'rtl'
  },
  banner: {
    backgroundColor: '#fff',
    borderRadius: 'var(--radius-lg, 12px)',
    boxShadow: '0 -2px 20px rgba(0,0,0,0.15)',
    padding: 'var(--sp-lg, 16px)',
    maxWidth: '400px',
    margin: '0 auto'
  },
  content: {
    display: 'flex',
    gap: 'var(--sp-md, 12px)',
    alignItems: 'center',
    marginBottom: 'var(--sp-md, 12px)'
  },
  text: {
    flex: 1
  },
  title: {
    fontSize: 'var(--font-size-base, 14px)',
    fontWeight: 700,
    color: 'var(--color-primary, #3E2C6E)',
    marginBottom: '2px'
  },
  subtitle: {
    fontSize: 'var(--font-size-xs, 12px)',
    color: 'var(--color-text-secondary, #666)',
    lineHeight: 1.4
  },
  actions: {
    display: 'flex',
    gap: 'var(--sp-sm, 8px)',
    justifyContent: 'flex-start'
  },
  installBtn: {
    padding: 'var(--sp-sm, 8px) var(--sp-xl, 20px)',
    fontSize: 'var(--font-size-sm, 13px)',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: 'var(--color-primary, #3E2C6E)',
    border: 'none',
    borderRadius: 'var(--radius-md, 8px)',
    cursor: 'pointer'
  },
  dismissBtn: {
    padding: 'var(--sp-sm, 8px) var(--sp-xl, 20px)',
    fontSize: 'var(--font-size-sm, 13px)',
    fontWeight: 500,
    color: 'var(--color-text-secondary, #666)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border, #ddd)',
    borderRadius: 'var(--radius-md, 8px)',
    cursor: 'pointer'
  }
};
